/**
 * Amend-menu-item dialog — repointed to the rebuilt `/api/menu/items/:id/amend`
 * contract (#1151 PR-5).
 *
 * The rebuilt backend amends a published item by REPLACING its dish
 * snapshot (`dishes`, a JSON array) with a stated `reason`
 * (`combo_changed | dish_substituted | cancelled`) — not the archived
 * from-dish → to-dish swap. It re-renders the Shopify title synchronously
 * (ADR-0002, no outbox) and returns `{ menuItemId, shopify }` where
 * `shopify` is `updated` (title rewritten) or `not_linked` (item had no
 * Shopify product). Errors are a plain `{ error: { code, message } }`
 * 409 (`Item's plan is X, not published`) / 404 — no `extras` sentinels.
 *
 * The client-side amend window (today / tomorrow) is retained as a
 * soft UX hint for MenuItemsCard button visibility; the rebuilt route no
 * longer enforces a window, so it is not a hard submit block.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToday } from '@resource-kit/controllers/react';
import {
  Modal,
  ModalBody,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  Button,
  Label,
} from '@resource-kit/ui/react';
import { useToast } from '@components/ui/Toast';
import { menuPlanAPI, MENU_AMEND_REASONS, type MenuAmendReason, type MenuItem } from '@/services';
import { queryKeys } from '@lib/queryKeys';
import { ApiError } from '@lib/api';

interface Props {
  open: boolean;
  onClose: () => void;
  planId: string;
  item: MenuItem;
}

/** The calendar day after `day` (both YYYY-MM-DD). */
function nextDay(day: string): string {
  const next = new Date(`${day}T00:00:00Z`);
  next.setUTCDate(next.getUTCDate() + 1);
  return next.toISOString().slice(0, 10);
}

/** The amend window — `today` and the day after it, as ISO YYYY-MM-DD
 *  strings. `today` is the merchant day the caller read through
 *  `useToday()`, so the window moves at merchant midnight with the rest
 *  of the app (#2786).
 *
 *  Exported for unit tests + the visibility helper that decides whether
 *  to surface the Amend button on a row (a soft UX gate to today /
 *  tomorrow deliveries). */
export function getAmendWindow(today: string): { today: string; tomorrow: string } {
  return { today, tomorrow: nextDay(today) };
}

/** True iff `deliveryDate` is in the amend window {today, tomorrow}.
 *  Exported alongside `getAmendWindow` so component-layer visibility
 *  checks read from the same predicate the dialog surfaces. */
export function isInAmendWindow(deliveryDate: string, today: string): boolean {
  const window = getAmendWindow(today);
  return deliveryDate === window.today || deliveryDate === window.tomorrow;
}

/** Parse the menu_item.dishes JSON snapshot into a display list. Returns
 *  `null` when the text is not a JSON array so the caller can surface a
 *  fail-loud validation message rather than sending a bad body. */
function parseDishArray(raw: string): unknown[] | null {
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/** Pretty-print the incoming snapshot so the operator edits readable
 *  JSON. Falls back to the raw string if it does not parse. */
function prettySnapshot(raw: string): string {
  const parsed = parseDishArray(raw);
  return parsed === null ? raw : JSON.stringify(parsed, null, 2);
}

export function AmendMenuItemDialog({ open, onClose, planId, item }: Props) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const toast = useToast();

  const today = useToday();
  const inWindow = isInAmendWindow(item.delivery_date, today);

  const [dishes, setDishes] = useState<string>(() => prettySnapshot(item.dishes));
  const [reason, setReason] = useState<MenuAmendReason | ''>('');
  const [localError, setLocalError] = useState<string | null>(null);

  const reset = () => {
    setDishes(prettySnapshot(item.dishes));
    setReason('');
    setLocalError(null);
  };

  const handleClose = () => {
    if (mutation.isPending) return;
    reset();
    onClose();
  };

  const mutation = useMutation({
    mutationFn: (body: { dishes: string; reason: MenuAmendReason }) =>
      menuPlanAPI.amendItem(item.id, body),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.menuPlans.detail(planId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.menuPlans.lists() });
      toast.success(
        result.shopify === 'not_linked'
          ? t('menu_plan.amend.successNotLinked')
          : t('menu_plan.amend.success'),
      );
      reset();
      onClose();
    },
    onError: (e) => {
      // The rebuilt backend returns a plain 409 ("Item's plan is X, not
      // published") / 404 with a human message and no `extras` sentinel,
      // so surface the server message directly.
      if (e instanceof ApiError) {
        toast.error(e.message || t('menu_plan.amend.error.unknown'));
        return;
      }
      toast.error(t('menu_plan.amend.error.unknown'));
    },
  });

  const handleSubmit = () => {
    setLocalError(null);
    if (!reason) {
      setLocalError(t('menu_plan.amend.error.reasonRequired'));
      return;
    }
    if (!dishes.trim()) {
      setLocalError(t('menu_plan.amend.error.dishesRequired'));
      return;
    }
    // Normalise to compact JSON so the backend stores a canonical
    // snapshot; fail loud if the operator's edit is not a JSON array.
    const parsed = parseDishArray(dishes);
    if (parsed === null) {
      setLocalError(t('menu_plan.amend.error.invalidJson'));
      return;
    }
    mutation.mutate({ dishes: JSON.stringify(parsed), reason });
  };

  return (
    <Modal isOpen={open} onClose={handleClose} size="md">
      <ModalHeader onClose={handleClose}>
        <ModalTitle>{t('menu_plan.amend.title')}</ModalTitle>
        <ModalDescription>
          {t('menu_plan.amend.description', { deliveryDate: item.delivery_date })}
        </ModalDescription>
      </ModalHeader>
      <ModalBody>
        <div className="space-y-4">
          {!inWindow && (
            <div className="rounded-md border border-warning/30 bg-warning/10 p-3 text-sm">
              {t('menu_plan.amend.outOfWindowBanner', {
                deliveryDate: item.delivery_date,
              })}
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="amend-reason">{t('menu_plan.amend.reasonLabel')}</Label>
            <select
              id="amend-reason"
              data-testid="menu-plan-amend-reason"
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={reason}
              onChange={(e) => {
                setReason(e.target.value as MenuAmendReason | '');
                setLocalError(null);
              }}
              disabled={mutation.isPending}
            >
              <option value="">{t('menu_plan.amend.reasonPlaceholder')}</option>
              {MENU_AMEND_REASONS.map((r) => (
                <option key={r} value={r}>
                  {t(`menu_plan.amend.reason.${r}`)}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="amend-dishes">{t('menu_plan.amend.dishesLabel')}</Label>
            <textarea
              id="amend-dishes"
              data-testid="menu-plan-amend-dishes"
              rows={6}
              className="w-full rounded-md border border-input bg-background px-3 py-2 font-mono text-xs"
              value={dishes}
              onChange={(e) => {
                setDishes(e.target.value);
                setLocalError(null);
              }}
              disabled={mutation.isPending}
            />
            <p className="text-xs text-muted-foreground">{t('menu_plan.amend.dishesHint')}</p>
          </div>
          {localError && (
            <p className="text-sm text-destructive" role="alert">
              {localError}
            </p>
          )}
        </div>
      </ModalBody>
      <ModalFooter>
        <Button variant="outline" onClick={handleClose} disabled={mutation.isPending}>
          {t('ui.cancel')}
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={mutation.isPending}
          data-testid="menu-plan-amend-submit"
        >
          {mutation.isPending ? t('menu_plan.amend.submitting') : t('menu_plan.amend.submit')}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
